/**
 * ============================================================================
 * AuditListPage — 감사(Audit) 로그 조회 + Excel Export
 * ============================================================================
 *
 * 【역할】
 *   모든 쓰기성 액션의 감사 로그 조회 — 규제 대응 / 사후 추적 / 이슈 분석.
 *   행위자(actor), 액션(action), 힌트(hint), 대상(target), 메타(JSON) 까지
 *   기록되며, 펼침 행에서 metaJson 을 포맷팅된 JSON 으로 표시.
 *
 * 【주요 기능】
 *   - 검색: 기간(dateFrom~dateTo) + actor + targetType + hint(Select)
 *   - 목록: auditId / hint(컬러 Tag) / actor / action / target / cid / createdAt
 *   - 펼침 행: metaJson 을 JSON.stringify(2-indent) 로 사람이 읽기 쉽게 표시
 *   - 서버사이드 Excel: exportServerSide → 동일 검색 조건으로 백엔드 generate
 *   - cid 클릭 복사 (Typography.Text copyable)
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=audit/searchList) — 감사 로그 조회
 *   - (server-side excel) POST /api/v1/excel/audit/searchList — 서버 Excel 생성
 *
 * 【DataSet/Hook】
 *   - useDataSet<AuditRow>() — listDs : 감사 로그 (PK: auditId, 조회 전용)
 *   - useTransaction()       — RPC 통합 호출
 *   - useExcel()             — exportServerSide 로 서버사이드 Excel
 *   - useLabel()             — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/audit', element: <AuditListPage /> }`
 * 【포팅 원본】 react-fastapi 신규 화면 (감사 대응 신규 추가)
 * 【의존성】 antd Button/Card/DatePicker/Form/Input/Select/Space/Table/Tag, dayjs
 */
import { useEffect, useState } from 'react'
import { Button, Card, DatePicker, Form, Input, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useTransaction } from '@/framework'
import { useDataSet, type TrackedRow } from '@/framework/useDataSet'
import { useExcel } from '@/framework/useExcel'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'

const { Title, Text } = Typography

/** 감사 로그 1행 — 모든 쓰기성 액션 추적. */
interface AuditRow {
  /** 로그 PK (DB 시퀀스). */
  auditId: number
  /** 분류 힌트 (예: "APPROVAL", "SECURITY") — 컬러 Tag 매핑. */
  hint: string
  /** 행위자 (loginId / system 계정명). */
  actor: string
  /** 수행 액션 (예: "approval.submit", "user.delete"). */
  action: string
  /** 대상 엔티티 타입 (예: "approval", "file", "user"). */
  targetType?: string
  /** 대상 식별자 (예: "12345", "uuid"). */
  targetId?: string
  /** 추가 메타 (JSON 문자열, 펼침 행에서 포맷팅 표시). */
  metaJson?: string
  /** Correlation ID (분산 추적용). */
  cid?: string
  /** 로그 생성 시각 (ISO 8601). */
  createdAt?: string
}

const HINT_COLORS: Record<string, string> = {
  APPROVAL: 'blue',
  STORAGE: 'purple',
  SECURITY: 'red',
  RUNWAY: 'orange',
  EXCEL: 'green',
  SCHED: 'default',
  AUDIT: 'cyan'
}

const HINT_OPTIONS = ['APPROVAL', 'STORAGE', 'SECURITY', 'RUNWAY', 'EXCEL', 'SCHED', 'AUDIT'].map(
  (h) => ({ value: h, label: h })
)

/**
 * AuditListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function AuditListPage() {
  const { t } = useLabel()
  const { transaction } = useTransaction()
  const { exportServerSide } = useExcel()
  const [form] = Form.useForm()
  const listDs = useDataSet<AuditRow>([])
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  const buildSearchPayload = () => {
    const v = form.getFieldsValue()
    return {
      hint: v.hint || undefined,
      actor: v.actor || undefined,
      targetType: v.targetType || undefined,
      dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
      dateTo: dateRange?.[1]?.format('YYYY-MM-DD')
    }
  }

  const search = () =>
    transaction({
      id: 'load',
      service: 'audit/searchList',
      action: 'search',
      inds: { ds_search: buildSearchPayload() },
      outds: { ds_audit: listDs }
    })

  useEffect(() => {
    search()
  }, [])

  const downloadExcel = () =>
    exportServerSide(
      'audit/searchList',
      { ds_search: buildSearchPayload() },
      [
        { key: 'auditId', header: '#', width: 10 },
        { key: 'hint', header: t('LBL_HINT', '힌트'), width: 14 },
        { key: 'actor', header: t('LBL_ACTOR', '행위자'), width: 18 },
        { key: 'action', header: 'Action', width: 22 },
        { key: 'targetType', header: t('LBL_TARGET', '대상'), width: 14 },
        { key: 'targetId', header: 'Target ID', width: 14 },
        { key: 'cid', header: 'CID', width: 40 },
        { key: 'createdAt', header: t('LBL_CREATED_AT', '생성일시'), width: 22 }
      ],
      `audit-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`
    )

  const columns: ColumnsType<TrackedRow<AuditRow>> = [
    { title: '#', dataIndex: ['data', 'auditId'], width: 90 },
    {
      title: t('LBL_HINT', '힌트'),
      dataIndex: ['data', 'hint'],
      width: 110,
      render: (v: string) => <Tag color={HINT_COLORS[v] || 'default'}>{v}</Tag>
    },
    { title: t('LBL_ACTOR', '행위자'), dataIndex: ['data', 'actor'], width: 140 },
    { title: 'Action', dataIndex: ['data', 'action'], width: 180 },
    {
      title: t('LBL_TARGET', '대상'),
      width: 220,
      render: (_v: unknown, r: TrackedRow<AuditRow>) =>
        r.data.targetType || r.data.targetId
          ? `${r.data.targetType ?? ''}${r.data.targetId ? '#' + r.data.targetId : ''}`
          : '-'
    },
    {
      title: 'CID',
      dataIndex: ['data', 'cid'],
      width: 280,
      render: (v?: string) => (
        <Text copyable={!!v} style={{ fontFamily: 'monospace', fontSize: 11 }}>
          {v || '-'}
        </Text>
      )
    },
    { title: t('LBL_CREATED_AT', '생성일시'), dataIndex: ['data', 'createdAt'], width: 200 }
  ]

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
        <Title level={3} style={{ margin: 0 }}>
          {t('LBL_AUDIT', '감사 로그')}
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={search}>
            {t('BTN_SEARCH', '조회')}
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={downloadExcel}>
            {t('BTN_EXCEL', 'Excel')}
          </Button>
        </Space>
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form form={form} layout="inline" onFinish={search}>
          <Form.Item name="hint" label={t('LBL_HINT', '힌트')}>
            <Select
              allowClear
              options={HINT_OPTIONS}
              style={{ width: 140 }}
              placeholder={t('PH_ALL', '전체')}
            />
          </Form.Item>
          <Form.Item name="actor" label={t('LBL_ACTOR', '행위자')}>
            <Input allowClear placeholder="admin" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="targetType" label={t('LBL_TARGET', '대상')}>
            <Input allowClear placeholder="approval / file / ..." style={{ width: 180 }} />
          </Form.Item>
          <Form.Item label={t('LBL_DATE_RANGE', '기간')}>
            <DatePicker.RangePicker
              value={dateRange as [Dayjs | null, Dayjs | null] | undefined}
              onChange={(r) => setDateRange(r as [Dayjs | null, Dayjs | null] | null)}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t('BTN_SEARCH', '조회')}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <DataTable<TrackedRow<AuditRow>>
        rowKey={(r) => String(r.data.auditId)}
        columns={columns}
        dataSource={listDs.getVisibleRows()}
        size="small"
        expandable={{
          rowExpandable: (r) => !!r.data.metaJson,
          expandedRowRender: (r) => (
            <pre
              style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {(() => {
                try {
                  return JSON.stringify(JSON.parse(r.data.metaJson ?? '{}'), null, 2)
                } catch {
                  return r.data.metaJson
                }
              })()}
            </pre>
          )
        }}
      />
    </PageContainer>
  )
}
